import { ButtonView } from '@vivli/shared/views';
import { blueButtonStyle, buttonContainerStyle, fullFlexColStyle, yellowButtonStyle } from './manage-vm.styles';
import { IVirtualMachine } from '@vivli/features/virtual-machine/infrastructure/interface';
import { first } from 'rxjs/operators';
import {
    useActiveUserContext,
    useModalApiContext,
    useToastApiContext,
    useVirtualMachineApiContext,
} from '@vivli/core/infrastructure/context';
import { AuditTrailContextualComponent } from '@vivli/features/admin-console/components';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

interface ManageVmModalButtonsComponentProps {
    vm: IVirtualMachine;
    isLoading: boolean;
    setIsLoading: (isLoading) => void;
}

export const ManageVmModalButtonsComponent = ({ vm, isLoading, setIsLoading }: ManageVmModalButtonsComponentProps) => {
    const modalService = useModalApiContext();
    const toastService = useToastApiContext();
    const virtualMachineService = useVirtualMachineApiContext();
    const { hasPermission } = useActiveUserContext();
    //TODO PERMFE - redo with permissions+
    const isOpsAdmin = hasPermission(PermissionsEnum.UseAdmConsoleAdv);
    const resourceGroupName = vm?.resourceGroupName ? vm?.resourceGroupName : 'N/A';
    const notDeProvisioned = vm?.status !== 'DeProvisioned';
    const adminAndNotDeProvisioned = isOpsAdmin && notDeProvisioned;

    const handleError = (e) => {
        modalService.error(e);
        setIsLoading(false);
    };

    const handleConfirmStop = () => {
        modalService.confirm('Are you sure you want to stop this Secure Research Environment?', {
            title: 'Stop Secure Research Environment',
            style: { zIndex: 11000 },
            onConfirm: () => vmStopConfirmResult(),
        });
    };

    const handleConfirmDeprovision = () => {
        modalService.confirm('Are you sure you want to deprovision? This action cannot be undone.', {
            title: 'Deprovision Secure Research Environment',
            style: { zIndex: 11000 },
            onConfirm: () => vmDeprovisionConfirmResult(),
        });
    };

    const startResearchEnvironment = () => {
        setIsLoading(true);
        virtualMachineService
            .startResearchEnvironment(vm.dataRequestId)
            .pipe(first())
            .subscribe(() => {
                setIsLoading(false);
                toastService.info(`Start request issued, please close the Dialog and check back.`);
            }, handleError);
    };

    const connectToVm = () => {
        window.open(`/virtual-machine/${vm.id}/${vm.dataRequestDoi}`);
    };

    const vmStopConfirmResult = () => {
        setIsLoading(true);
        virtualMachineService
            .stopResearchEnvironment(vm.id)
            .pipe(first())
            .subscribe((vm) => {
                toastService.info(`Stop request issued, please close the Dialog and check back.`);
            }, handleError);
    };

    const vmDeprovisionConfirmResult = () => {
        setIsLoading(true);
        virtualMachineService
            .deprovisionResearchEnvironment(vm.id)
            .pipe(first())
            .subscribe((vm) => {
                setIsLoading(false);
                toastService.info(`Deprovision request issued, please close the Dialog and check back.`);
            }, handleError);
    };

    const getPassword = () => {
        virtualMachineService.getAdminPassword(vm.id).subscribe((token) => {
            navigator.clipboard.writeText(token);
            toastService.info(`Password copied to the clipboard.`);
        }, handleError);
    };

    return (
        <>
            <div style={fullFlexColStyle}>
                <div style={buttonContainerStyle}>
                    <ButtonView style={yellowButtonStyle} onClick={handleConfirmStop} disabled={isLoading}>
                        Stop Secure Research Environment
                    </ButtonView>
                    {notDeProvisioned && (
                        <ButtonView style={yellowButtonStyle} onClick={handleConfirmDeprovision} disabled={isLoading}>
                            Deprovision Secure Research Environment
                        </ButtonView>
                    )}
                    <ButtonView style={blueButtonStyle} onClick={startResearchEnvironment} disabled={isLoading}>
                        Start Secure Research Environment
                    </ButtonView>
                    {adminAndNotDeProvisioned && (
                        <>
                            <ButtonView style={blueButtonStyle} onClick={connectToVm}>
                                Connect to VM
                            </ButtonView>
                            <AuditTrailContextualComponent
                                type="VM"
                                id={vm.dataRequestDoi}
                                internalId={vm.id}
                            ></AuditTrailContextualComponent>
                        </>
                    )}
                    {isOpsAdmin && (
                        <ButtonView style={blueButtonStyle} onClick={getPassword}>
                            Get Password
                        </ButtonView>
                    )}
                </div>
            </div>
        </>
    );
};
